import React, { useState } from 'react';
import { createActivityLogEntry, getOppsContactId, updateOppsPipelineStage } from '../api/monday';

// ── Constants ──────────────────────────────────────────────────────────────

const ACTIVITY_TYPES = [
  'Prospect call',
  'Owner call',
  'Text',
  'In Person',
  'Email',
  'Other',
];

const OUTCOMES_BY_TYPE = {
  'Prospect call': ['Reached', 'No answer / left message'],
  'Owner call':    ['Reached', 'No answer / left message'],
  'Text':          ['Reached', 'No response'],
  'In Person':     ['Met', 'No show'],
  'Email':         ['Email sent'],
  'Other':         ['Reached', 'No answer / left message'],
};

const OUTCOME_COLORS = {
  'Reached':                  { bg: '#E1F5EE', color: '#085041', border: '#6ECFAA' },
  'No answer / left message': { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
  'No response':              { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
  'Email sent':               { bg: '#EEEDFE', color: '#3C3489', border: '#A09CD6' },
  'Met':                      { bg: '#E1F5EE', color: '#085041', border: '#6ECFAA' },
  'No show':                  { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
};

// Pipeline stage sequence — used for next-stage advancement and guidance
const STAGE_SEQUENCE = [
  'Contact Added',
  'Email 1',      'Phone Call 1',
  'Email 2',      'Phone Call 2',
  'Email 3',      'Phone Call 3',
  'Email 4',      'Phone Call 4',
  'Email 5',      'Phone Call 5',
  'Connection Established',
];

// Returns the next stage in the sequence, or null if at end / unknown
function getNextStage(current) {
  const idx = STAGE_SEQUENCE.indexOf(current);
  if (idx < 0 || idx >= STAGE_SEQUENCE.length - 1) return null;
  return STAGE_SEQUENCE[idx + 1];
}

// Returns which activity types best match the current stage
function getRecommendedTypes(stage) {
  if (!stage) return [];
  const s = stage.toLowerCase();
  if (s.includes('email') || stage === 'Contact Added') return ['Email'];
  if (s.includes('phone call'))                         return ['Prospect call', 'Owner call'];
  return [];
}

// Returns the best default activity type for the given stage
function getDefaultType(stage) {
  const recs = getRecommendedTypes(stage);
  return recs[0] || 'Prospect call';
}

// Human-readable guidance blurb shown below the stage badge
function getStageGuidance(stage) {
  if (!stage) return null;
  const s = stage.toLowerCase();
  if (s.includes('phone call')) return 'A call is up next — reach out by phone if you can.';
  if (s.includes('email'))      return 'An email is up next — send one or log any touch you made.';
  if (stage === 'Contact Added') return 'Just added — an introductory email is a great first step.';
  if (stage === 'Connection Established') return 'You\'re connected — log whatever you discussed.';
  return null;
}

// ── Styles ─────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily: "'Inter', 'Segoe UI', system-ui, Arial, sans-serif",
    maxWidth: '720px', margin: '0 auto',
    minHeight: '100vh', backgroundColor: '#f5f4f1',
    display: 'flex', flexDirection: 'column',
  },
  nav: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e8e6e0',
    position: 'sticky', top: 0, zIndex: 10,
  },
  backBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '22px', color: '#374151', padding: '0 4px', lineHeight: 1,
  },
  navTitle: { fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 },
  navSub:   { fontSize: '13px', color: '#9ca3af', marginLeft: 'auto' },
  body:     { flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' },
  section:  { backgroundColor: '#fff', borderRadius: '12px', padding: '16px 18px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  sectionTitle: { fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' },
  required: { color: '#dc2626', marginLeft: '3px' },
  pillGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  pill:     { padding: '8px 14px', borderRadius: '99px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: '1.5px solid', transition: 'all 0.12s', background: 'none' },
  textarea: { width: '100%', minHeight: '90px', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px', fontSize: '14px', color: '#111827', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none' },
  hint:     { fontSize: '12px', color: '#dc2626', marginTop: '6px' },
  footer:   { padding: '0 0 24px' },
  saveBtn:  { width: '100%', padding: '15px', borderRadius: '10px', border: 'none', fontSize: '16px', fontWeight: '700', cursor: 'pointer', transition: 'opacity 0.15s' },
  cancelBtn:{ width: '100%', padding: '12px', marginTop: '8px', borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '14px', fontWeight: '500', cursor: 'pointer', background: '#fff', color: '#374151' },
  saving:   { textAlign: 'center', padding: '40px 20px', color: '#6b7280' },
  errorBox: { background: '#fef2f2', borderRadius: '8px', padding: '14px', color: '#b91c1c', fontSize: '13px' },

  // Stage context banner
  stageBanner: {
    backgroundColor: '#fff', borderRadius: '12px',
    padding: '14px 18px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
  },
  stageLabel: { fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em' },
  stageBadge: {
    fontSize: '13px', fontWeight: '600',
    backgroundColor: '#EEEDFE', color: '#3C3489',
    padding: '3px 10px', borderRadius: '99px',
  },
  stageArrow: { fontSize: '12px', color: '#6b7280', marginLeft: 'auto' },
  stageGuidance: { fontSize: '12px', color: '#6b7280', marginTop: '8px', width: '100%' },

  // "Recommended" tag shown on suggested pill types
  recTag: {
    fontSize: '10px', fontWeight: '700',
    color: '#1D9E75', marginLeft: '5px',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
};

// ── Main component ─────────────────────────────────────────────────────────
export default function OppsLogActivity({ oppsItem, brokerName, onSaved, onCancel }) {
  const currentStage    = oppsItem.column_values?.find((c) => c.id === 'status')?.text || '';
  const nextStage       = getNextStage(currentStage);
  const recommendedTypes = getRecommendedTypes(currentStage);
  const guidance        = getStageGuidance(currentStage);

  const [activityType, setActivityType] = useState(getDefaultType(currentStage));
  const [outcome, setOutcome]           = useState('');
  const [notes, setNotes]               = useState('');
  const [saving, setSaving]             = useState(false);
  const [saved, setSaved]               = useState(false);
  const [error, setError]               = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  const contactId = getOppsContactId(oppsItem);
  const canSave   = !!outcome;

  async function handleSave() {
    if (!canSave) { setShowValidation(true); return; }
    setSaving(true);
    setError(null);
    try {
      await createActivityLogEntry({
        contactItemId: contactId,
        prospectingItemName: oppsItem.name,
        activityType,
        outcome,
        notes,
        nextFollowUpDate: null,   // Opps is stage-driven, no date snooze needed
        stageAtLog: currentStage,
        brokerName,
      });

      // Advance pipeline stage — fire-and-forget, non-blocking
      if (nextStage) {
        updateOppsPipelineStage(oppsItem.id, nextStage).catch((err) =>
          console.warn('[OppsLogActivity] Stage advance failed:', err.message)
        );
      }

      setSaved(true);
      setTimeout(() => onSaved(), 1200);
    } catch (err) {
      setError(err.message || 'Save failed. Please try again.');
      setSaving(false);
    }
  }

  if (saving && !error) {
    return (
      <div style={s.page}>
        <div style={s.saving}>
          {saved
            ? `✅ Saved!${nextStage ? ` Stage → ${nextStage}` : ''} Returning…`
            : 'Saving activity…'}
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      {/* Nav */}
      <div style={s.nav}>
        <button style={s.backBtn} onClick={onCancel}>←</button>
        <p style={{ ...s.navTitle, margin: 0 }}>Log Activity</p>
        <span style={s.navSub}>{(oppsItem.name || '').split('|')[0].trim()}</span>
      </div>

      <div style={s.body}>
        {error && <div style={s.errorBox}><strong>Error:</strong> {error}</div>}

        {/* Stage context banner */}
        {currentStage && (
          <div style={s.stageBanner}>
            <span style={s.stageLabel}>Current stage</span>
            <span style={s.stageBadge}>{currentStage}</span>
            {nextStage && (
              <span style={s.stageArrow}>→ {nextStage} after save</span>
            )}
            {guidance && (
              <span style={s.stageGuidance}>{guidance}</span>
            )}
          </div>
        )}

        {/* Activity type */}
        <div style={s.section}>
          <p style={s.sectionTitle}>Activity Type</p>
          <div style={s.pillGrid}>
            {ACTIVITY_TYPES.map((type) => {
              const selected    = activityType === type;
              const isRecommended = recommendedTypes.includes(type);
              return (
                <button
                  key={type}
                  style={{
                    ...s.pill,
                    backgroundColor: selected ? '#534AB7' : '#fff',
                    color: selected ? '#fff' : '#374151',
                    borderColor: selected
                      ? '#534AB7'
                      : isRecommended
                        ? '#1D9E75'
                        : '#e8e6e0',
                    boxShadow: isRecommended && !selected
                      ? '0 0 0 1px #1D9E75'
                      : 'none',
                  }}
                  onClick={() => { setActivityType(type); setOutcome(''); }}
                >
                  {type}
                  {isRecommended && !selected && (
                    <span style={s.recTag}>↑</span>
                  )}
                </button>
              );
            })}
          </div>
          {recommendedTypes.length > 0 && (
            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
              Green border = recommended for this stage. All types can be logged.
            </p>
          )}
        </div>

        {/* Outcome */}
        <div style={s.section}>
          <p style={s.sectionTitle}>Outcome <span style={s.required}>*</span></p>
          <div style={s.pillGrid}>
            {(OUTCOMES_BY_TYPE[activityType] || []).map((o) => {
              const selected = outcome === o;
              const colors   = OUTCOME_COLORS[o];
              return (
                <button
                  key={o}
                  style={{
                    ...s.pill,
                    backgroundColor: selected ? colors.bg   : '#fff',
                    color:           selected ? colors.color : '#6b7280',
                    borderColor:     selected ? colors.border : '#e5e7eb',
                    fontWeight:      selected ? '700' : '500',
                  }}
                  onClick={() => setOutcome(o)}
                >
                  {o}
                </button>
              );
            })}
          </div>
          {showValidation && !outcome && (
            <p style={s.hint}>Select an outcome before saving.</p>
          )}
        </div>

        {/* Notes */}
        <div style={s.section}>
          <p style={s.sectionTitle}>
            Notes <span style={{ color: '#9ca3af', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
          </p>
          <textarea
            style={s.textarea}
            placeholder="What happened? What did they say?"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {/* Save */}
        <div style={s.footer}>
          <button
            style={{
              ...s.saveBtn,
              backgroundColor: canSave ? '#1D9E75' : '#e5e7eb',
              color:           canSave ? '#fff'    : '#9ca3af',
              cursor:          canSave ? 'pointer' : 'not-allowed',
            }}
            onClick={handleSave}
          >
            Save Activity
          </button>
          <button style={s.cancelBtn} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
